// Pure FTMS (Fitness Machine Service) protocol encoding/decoding.
// No Bluetooth, no React, no state — everything here is unit-testable.
//
// Reference: Bluetooth SIG, Fitness Machine Service v1.0.

export const FTMS_OPCODE = Object.freeze({
  REQUEST_CONTROL: 0x00,
  RESET: 0x01,
  SET_TARGET_POWER: 0x05,
  SET_INDOOR_BIKE_SIMULATION: 0x11,
});

// Fitness Machine Feature characteristic.
export const FTMS_FEATURE_CHARACTERISTIC_UUID = 0x2acc;

// Values used when handing resistance back to the rider: flat road, no wind.
// Crr and Cw are ordinary road-bike defaults; they only shape how resistance
// grows with speed, they do not lock the rider to any power.
export const FLAT_ROAD_PARAMS = Object.freeze({
  windSpeedMps: 0,
  gradePercent: 0,
  crr: 0.004,
  cw: 0.51,
});

// Resolutions defined by the specification for opcode 0x11.
const WIND_SPEED_RESOLUTION = 0.001; // m/s
const GRADE_RESOLUTION = 0.01; // percent
const CRR_RESOLUTION = 0.0001; // unitless
const CW_RESOLUTION = 0.01; // kg/m

export function buildResetFrame() {
  const buffer = new ArrayBuffer(1);
  new DataView(buffer).setUint8(0, FTMS_OPCODE.RESET);
  return buffer;
}

export function buildFlatSimulationFrame(params) {
  const { windSpeedMps, gradePercent, crr, cw } = { ...FLAT_ROAD_PARAMS, ...(params || {}) };

  const buffer = new ArrayBuffer(7);
  const dv = new DataView(buffer);
  dv.setUint8(0, FTMS_OPCODE.SET_INDOOR_BIKE_SIMULATION);
  // Math.round, never truncation: 0.004 / 0.0001 is 40.000000000000007 in IEEE 754.
  dv.setInt16(1, Math.round(windSpeedMps / WIND_SPEED_RESOLUTION), true);
  dv.setInt16(3, Math.round(gradePercent / GRADE_RESOLUTION), true);
  dv.setUint8(5, Math.round(crr / CRR_RESOLUTION));
  dv.setUint8(6, Math.round(cw / CW_RESOLUTION));
  return buffer;
}

// The Fitness Machine Feature characteristic is 8 bytes:
//   bytes 0-3 : Fitness Machine Features   (uint32 LE)
//   bytes 4-7 : Target Setting Features    (uint32 LE)
// Bit 13 of Target Setting Features = Indoor Bike Simulation Parameters Supported.
const TARGET_SETTING_FEATURES_OFFSET = 4;
const INDOOR_BIKE_SIMULATION_BIT = 13;

export function parseIndoorBikeSimulationSupported(dataView) {
  if (!dataView || typeof dataView.getUint32 !== "function") return false;
  if (typeof dataView.byteLength !== "number" || dataView.byteLength < 8) return false;

  const targetSettingFeatures = dataView.getUint32(TARGET_SETTING_FEATURES_OFFSET, true);
  // >>> keeps this unsigned so a set bit 31 cannot sign-extend and poison the test.
  return ((targetSettingFeatures >>> INDOOR_BIKE_SIMULATION_BIT) & 1) === 1;
}
